import type { User } from 'firebase/auth'
import type USERS from '~/consts/users'
import type { ResolveType, SnakeToPascal } from '~/lib/types'

export interface UserSettings {
    handedness?: Handedness & string
    preferredMeasurementSystem?: 'metric' | 'us' | string
}

export type TechDiscUser = {
    settings?: UserSettings
    roles?: UserRoleId[]
} & User

export type UserRolePermissions = {
    simulator: string[]
}

export type UserRoleDocument = {
    name: string
    description: string
    claimToken: AuthClaim | PremiumFeature
    permissions: UserRolePermissions
    external?: boolean
}

export type UserPermissions = {
    simulator: string[]
}

export type UserAdminDocument = {
    email: string
    roles: UserRole['id'][]
    permissions: UserPermissions
    photoURL?: string
    displayName?: string
}

export type UserAdminInfo = ResolveType<UserAdminDocument & { id: string }>

export type UserRole = ResolveType<
    UserRoleDocument & {
        id: string
    }
>
export type UserAuthToken = PremiumFeature | AuthClaim

export type UserRoleId = (typeof USERS.ROLES)[keyof typeof USERS.ROLES]
export type UserRoles = Record<UserRoleFlag, boolean>
export type UserRoleFlag = `is${SnakeToPascal<keyof typeof USERS.ROLES>}`

export interface UserAccess {
    claims: AuthClaim[]
    premiumStatus: PremiumFeature
}

export interface UserProfile {
    photoUrl?: string
    displayName?: string
    pdgaNumber?: string
}

export interface UserDisplayMetadata {
    name?: string
    pdgaNumber?: string
    photo?: string
}

export enum Handedness {
    LEFT = 'LEFT',
    RIGHT = 'RIGHT',
}

export enum PremiumFeature {
    NONE = 'none', // this is the default value
    STORE = 'store_features',
    PLAY = 'play_features',
    PRO = 'pro_features',
}

export enum AuthClaim {
    admin = 'admin', // admin users can give these roles to other users and read and write everything
    manager = 'manager', // manager users can read everything and write to non-manager/admins
    debug = 'debug', // read everything
    debugThrows = 'debugThrows', // has access to throw debugging tools
    store = 'store', // verified store users can enter their address to get on the map
    beta = 'beta', // beta users can test out new features that are almost ready
    devices = 'devices', // This is for users that can interact with the devices page
    dev = 'dev', // dev users can see features that are in progress but not ready for beta plus debug tools/views
    earlyAccess = 'earlyAccess', // early access to new features, agreed to additional feedback requests
}

export type TokenClaim = AuthClaim | PremiumFeature
export type TokenClaims = TokenClaim[]
